<template>
  <v-app>
    <v-app-bar app elevation="0" color="background-secondary" height="64" class="app-layout-navbar px-3 sm:px-4" flat>
      <AppNavbar :is-mobile="isMobile" @toggle-drawer="drawerOpen = !drawerOpen" />
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawerOpen"
      app
      :temporary="isMobile"
      :permanent="!isMobile"
      :width="sidebarWidth"
      color="background-sidebar"
      class="app-navigation-drawer"
    >
      <AppSidebar />
    </v-navigation-drawer>

    <v-main class="app-layout__main">
      <div class="app-layout__content">
        <AppLayoutNavigation class="app-layout__breadcrumbs" />
        <main class="app-layout__page">
          <article>
            <RouterView />
          </article>
        </main>
      </div>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppDisplay } from '@/composables/ui/useAppDisplay'
import { useSnackbar } from '@/composables/ui/useSnackbar'
import { ALL_STORES_VALUE } from '../stores/auth-store'
import { useAuthStore } from '../stores/auth-store'
import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const authStore = useAuthStore()
const router = useRouter()
const { init: notify } = useSnackbar()
const breakpoints = useAppDisplay()

const sidebarWidth = 280
const drawerOpen = ref(true)

/** Collapsible overlay drawer below md (640px); permanent sidebar at md+ */
const isMobile = computed(() => breakpoints.smDown.value)

watch(
  isMobile,
  (mobile) => {
    drawerOpen.value = !mobile
  },
  { immediate: true },
)

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (isMobile.value) {
      drawerOpen.value = false
    }
    showStoreScopeSwitchToastIfNeeded()
  },
)

const showStoreScopeSwitchToastIfNeeded = () => {
  try {
    const raw = sessionStorage.getItem('store-scope-switch-toast')
    if (!raw) return
    sessionStorage.removeItem('store-scope-switch-toast')
    const payload = JSON.parse(raw)
    const storeName = typeof payload?.storeName === 'string' ? payload.storeName : 'your selected store'

    notify({
      color: 'warning',
      duration: 9000,
      message: `Switched to ${storeName}. This page requires a single store. Click to go to Dashboard (All Stores).`,
      onClick: () => {
        localStorage.setItem('selected_store', ALL_STORES_VALUE)
        const firstStoreId = authStore.userStores?.[0]?.id
        if (firstStoreId != null) {
          localStorage.setItem('shop_id', String(firstStoreId))
        }
        authStore.syncSelectedStoreLocalIdFromStorage()
        window.location.href = router.resolve({ name: 'dashboard-all-stores' }).href
      },
    })
  } catch {
    // ignore malformed session payload
  }
}

onMounted(() => {
  showStoreScopeSwitchToastIfNeeded()
})
</script>

<style lang="scss" scoped>
.app-layout__main {
  background-color: rgb(var(--v-theme-background-primary));
  min-width: 0;
}

.app-layout__content {
  min-height: 100%;
  min-width: 0;
  max-width: 100%;
  padding-bottom: var(--crisp-chat-offset, 80px);
  overflow-x: clip;
}

.app-layout__breadcrumbs {
  padding: 0.75rem 1rem 0;

  @media (min-width: 640px) {
    padding: 1rem 1.5rem 0;
  }
}

.app-layout__page {
  padding: 0.5rem 1rem 1.25rem;
  min-width: 0;

  @media (min-width: 640px) {
    padding: 0.75rem 1.5rem 1.5rem;
  }
}

.app-layout-navbar {
  border-bottom: 1px solid rgb(var(--v-theme-background-border));
}

.app-layout-navbar :deep(.v-toolbar__content) {
  padding-inline: 0;
  gap: 0.35rem;
  min-width: 0;
  overflow: visible;
}

@media (max-width: 639px) {
  .app-layout-navbar :deep(.v-toolbar__content) {
    gap: 0.25rem;
  }
}

.app-navigation-drawer {
  border-right: 1px solid rgb(var(--v-theme-background-border)) !important;
}

.app-navigation-drawer :deep(.v-navigation-drawer__content) {
  overflow-y: auto;
}
</style>
